import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Sparkles, Zap, Users } from 'lucide-react';
import SITE_CONFIG from '../config/siteConfig';
import PublicStats from '../components/PublicStats';
import CarouselSlider from '../components/CarouselSlider';

const HomePage = () => {
  return (
    <>
      <Helmet>
        <title>AI Carousel Generator - Create Stunning Social Media Posts</title>
        <link rel="canonical" href={SITE_CONFIG.siteUrl} />
        <meta 
          name="description" 
          content="Create professional carousel posts for Instagram, LinkedIn, and Facebook with AI. Generate engaging content in minutes." 
        />
        <meta 
          name="keywords" 
          content="AI carousel, social media, Instagram carousel, LinkedIn posts, content creation, AI generator, social media automation" 
        />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="AI Carousel Generator - Create Stunning Social Media Posts" />
        <meta property="og:description" content="Create professional carousel posts for Instagram, LinkedIn, and Facebook with AI." />
        <meta property="og:url" content={SITE_CONFIG.siteUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="AI Carousel Generator" />
        <meta name="twitter:description" content="Create professional carousel posts with AI" />
      </Helmet>
      
      <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-indigo-100 py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            
            {/* Left Side - Carousel Slider (First on mobile, left on desktop) */}
            <div className="order-1 lg:order-1">
              <CarouselSlider />
            </div>

            {/* Right Side - Text Content */}
            <div className="order-2 lg:order-2 text-center lg:text-left">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
                <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Create Stunning
                </span>
                <span className="block text-gray-900 mt-2">
                  Social Media Carousels
                </span>
              </h1>
              
              <p className="text-lg md:text-xl text-gray-600 mb-8 max-w-2xl mx-auto lg:mx-0">
                The fastest way to create professional carousel posts for LinkedIn, Instagram, and more. 
                No design skills required - just your ideas and our AI-powered editor.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link 
                  to="/create" 
                  className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl transition-shadow"
                >
                  <span>Start Creating Free</span>
                  <ArrowRight className="h-5 w-5" />
                </Link>
                <Link 
                  to="/pricing" 
                  className="bg-white text-primary-600 hover:bg-gray-50 border-2 border-primary-600 font-medium text-lg px-8 py-4 rounded-lg transition-colors duration-200 inline-flex items-center justify-center space-x-2"
                >
                  <span>View Pricing</span>
                </Link>
              </div>

              {/* Trust Badges */}
              <div className="mt-8 flex flex-wrap items-center justify-center lg:justify-start gap-6 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="font-medium">No Credit Card Required</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  <span className="font-medium">5-Star Rated</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Why Choose AI Carousels?
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to create engaging carousel content
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                <Sparkles className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                AI-Powered Design
              </h3>
              <p className="text-gray-600">
                Let our AI help you create beautiful designs with smart suggestions 
                and automated layouts that look professional.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                <Zap className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Lightning Fast
              </h3>
              <p className="text-gray-600">
                Create carousel posts in minutes, not hours. Our intuitive editor 
                makes it easy to design, edit, and export your content.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                <Users className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Multi-Platform Ready
              </h3>
              <p className="text-gray-600">
                Export your carousels in the perfect dimensions for LinkedIn, 
                Instagram, Facebook, and other social platforms.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Public Stats Section */}
      <PublicStats />

      {/* CTA Section */}
      <section className="bg-gray-100 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Ready to Create Amazing Carousels?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Join thousands of creators who trust AI Carousels for their social media content.
          </p>
          <Link 
            to="/login" 
            className="bg-primary-600 text-white hover:bg-primary-700 font-medium py-3 px-8 rounded-lg transition-colors duration-200 inline-flex items-center space-x-2"
          >
            <span>Get Started Free</span>
            <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </section>
      </div>
    </>
  );
};

export default HomePage;
